import { useRef, useState, type ChangeEvent } from "react";
import { useI18n } from "../i18n/useI18n";
import { numberLocaleFor } from "../i18n/locale";
import type { UiKey } from "../i18n/ui";
import { parseCostExplorerCsv } from "../lib/costExplorerCsv";

type Props = {
  t: (key: UiKey) => string;
  value: string;
  onChange: (s: string) => void;
  modelMonthlyUsd: number;
  /** Після успішного імпорту CSV — сирі суми по сервісах для порівняння з рядками моделі. */
  onExplorerParsed?: (byService: Record<string, number>) => void;
};

export function ActualBillCard({
  t,
  value,
  onChange,
  modelMonthlyUsd,
  onExplorerParsed,
}: Props) {
  const { locale, tf } = useI18n();
  const csvRef = useRef<HTMLInputElement>(null);
  const [csvMsg, setCsvMsg] = useState<"idle" | "ok" | "bad">("idle");
  const nf = numberLocaleFor(locale);
  const parsed = (() => {
    const raw = value.trim().replace(/,/g, "");
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const pct =
    parsed !== null && modelMonthlyUsd > 0
      ? Math.round(((parsed - modelMonthlyUsd) / modelMonthlyUsd) * 1000) / 10
      : null;

  function onCsvPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      const res = parseCostExplorerCsv(text);
      if (res.totalUsd == null || !Number.isFinite(res.totalUsd)) {
        setCsvMsg("bad");
        setTimeout(() => setCsvMsg("idle"), 3500);
        return;
      }
      onChange(String(Math.round(res.totalUsd)));
      if (onExplorerParsed && Object.keys(res.byService).length > 0) {
        onExplorerParsed(res.byService);
      }
      setCsvMsg("ok");
      setTimeout(() => setCsvMsg("idle"), 2500);
    };
    reader.onerror = () => {
      setCsvMsg("bad");
      setTimeout(() => setCsvMsg("idle"), 3500);
    };
    reader.readAsText(file, "utf-8");
  }

  return (
    <div className="actual-bill">
      <h2>{t("actualBill.title")}</h2>
      <p className="hint">{t("actualBill.hint")}</p>
      <label className="label">
        <span className="label-text">{t("actualBill.label")}</span>
        <input
          type="text"
          inputMode="decimal"
          className="input"
          placeholder={t("actualBill.placeholder")}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
        />
      </label>
      {parsed !== null && modelMonthlyUsd > 0 && pct !== null && (
        <p className="delta">
          {tf("actualBill.vsModel", {
            actual: parsed.toLocaleString(nf, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }),
            model: modelMonthlyUsd.toLocaleString(nf, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }),
            pct: String(pct),
          })}
        </p>
      )}
      {parsed === null && value.trim() !== "" && (
        <p className="warn">{t("actualBill.invalid")}</p>
      )}
      <div className="csv-row">
        <input
          ref={csvRef}
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          aria-label={t("csv.importButton")}
          onChange={onCsvPick}
        />
        <button
          type="button"
          className="csv-btn"
          onClick={() => csvRef.current?.click()}
        >
          {t("csv.importButton")}
        </button>
      </div>
      <p className="csv-hint">{t("csv.importHint")}</p>
      {csvMsg === "ok" && <p className="delta csv-msg">{t("csv.importOk")}</p>}
      {csvMsg === "bad" && <p className="warn csv-msg">{t("csv.importBad")}</p>}
      <style>{`
        .actual-bill h2 {
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
        .hint {
          margin: 0 0 0.65rem;
          font-size: 0.82rem;
          color: var(--muted);
          line-height: 1.45;
        }
        .label {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }
        .label-text {
          font-size: 0.85rem;
          color: var(--muted);
        }
        .input {
          max-width: 12rem;
          padding: 0.45rem 0.6rem;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--surface2);
          color: var(--text);
        }
        .input:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 1px;
        }
        @media (max-width: 720px) {
          .input {
            max-width: none;
            width: 100%;
            min-height: 48px;
            font-size: 1rem;
          }
        }
        .delta {
          margin: 0.65rem 0 0;
          font-size: 0.88rem;
          color: var(--accent);
        }
        .warn {
          margin: 0.5rem 0 0;
          font-size: 0.82rem;
          color: var(--danger);
        }
        .csv-row {
          margin-top: 0.75rem;
        }
        .csv-btn {
          border: 1px solid var(--border);
          background: var(--surface2);
          color: var(--text);
          padding: 0.4rem 0.65rem;
          border-radius: 8px;
          font: inherit;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
        }
        .csv-btn:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 1px;
        }
        .csv-hint {
          margin: 0.4rem 0 0;
          font-size: 0.75rem;
          color: var(--muted);
          line-height: 1.4;
        }
        .csv-msg {
          margin-top: 0.35rem;
        }
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      `}</style>
    </div>
  );
}
